import React from "react";

import { css, cx } from "@emotion/css";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import { useStyles } from "@/hooks/useStyles.ts";
import { useWindowSize } from "@/hooks/useWindowSize.ts";
import { CurrentCarePlanInfo } from "@/models/CarePlanModel.ts";
import { Condition, diabetesTypeTranslationMapping } from "@/models/ConditionModel.ts";
import { Patient } from "@/models/PatientModel.ts";
import { userFormattedName } from "@/models/UserModel.ts";
import { Queries } from "@/queries/Queries.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { ScreenMax, ScreenSizes } from "@/theme/responsive.ts";
import { Age } from "@/uiKit/atoms/Age.tsx";
import { Tooltip } from "@/uiKit/atoms/Tooltip.tsx";
import { Typography } from "@/uiKit/atoms/Typography.tsx";
import { TelemonitoringActivityTag } from "@/uiKit/molecules/TelemonitoringActivityTag.tsx";

export type PatientHeaderInfoProps = {
  patient: Patient;
  className?: string;
};

export const PatientHeaderInfo: React.FC<PatientHeaderInfoProps> = ({ patient, className }) => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);
  const condition = Queries.condition.useCondition(patient?.id);
  const currentCarePlanInfo = Queries.practitioner.useCurrentCarePlanInfo(patient.id);
  const { width } = useWindowSize();

  return (
    <div className={cx(styles.container, className, "scrollbar")}>
      <Tooltip
        popupId="patient-header-info-tooltip"
        placement="bottom-start"
        content={
          <PatientHeaderInfoMobile patient={patient} condition={condition} currentCarePlanInfo={currentCarePlanInfo} />
        }
        disabled={width > ScreenSizes.MD}
      >
        <div className={styles.infos}>
          <Typography variant="subtitleLarge" className={styles.text}>
            {userFormattedName(patient.familyName, patient.givenName)}
          </Typography>
          <Age birthDate={patient.birthDate} className={styles.text} />
          <Typography variant="body" className={cx(styles.text)}>
            {t(diabetesTypeTranslationMapping(condition.diabetesType, condition.medicationSchema))}
          </Typography>
          {condition.termDate && (
            <Typography variant="body">
              {`${t("condition.term")} : `}
              {DateTime.fromISO(condition.termDate).toLocaleString(DateTime.DATE_SHORT)}
            </Typography>
          )}
          <TelemonitoringActivityTag
            className={styles.tag}
            tags={currentCarePlanInfo.telemonitoringTags}
            format="long"
          />
        </div>
      </Tooltip>
    </div>
  );
};

type PatientHeaderInfoMobileProps = {
  patient: Patient;
  condition: Condition;
  currentCarePlanInfo: CurrentCarePlanInfo;
  className?: string;
};

const PatientHeaderInfoMobile: React.FC<PatientHeaderInfoMobileProps> = ({
  patient,
  condition,
  currentCarePlanInfo,
  className,
}) => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);

  return (
    <div className={cx(styles.mobileContainer, className)}>
      <Typography variant="subtitleLarge" className={styles.text}>
        {userFormattedName(patient.familyName, patient.givenName)}
      </Typography>
      <Age birthDate={patient.birthDate} className={styles.text} />
      <Typography variant="body" className={cx(styles.text)}>
        {t(diabetesTypeTranslationMapping(condition.diabetesType, condition.medicationSchema))}
      </Typography>
      {condition.termDate && (
        <Typography variant="body">
          {`${t("condition.term")} : `}
          {DateTime.fromISO(condition.termDate).toLocaleString(DateTime.DATE_SHORT)}
        </Typography>
      )}
      <TelemonitoringActivityTag className={styles.tag} tags={currentCarePlanInfo.telemonitoringTags} format="long" />
    </div>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    flex: 1 1 0;
    overflow-x: hidden;
  `,
  infos: css`
    display: flex;
    flex-direction: row;
    flex: 1 1;
    align-items: baseline;
    gap: ${theme.spacing(12)};
    ${ScreenMax.MD} {
      gap: ${theme.spacing(4)};
    }
  `,
  mobileContainer: css({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(4),
    padding: theme.spacing(4),
  }),
  text: css({
    whiteSpace: "nowrap",
  }),
  tag: css({}),
});
